"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Loader } from "lucide-react";
const directoryEntries = [{
  label: "COORDINATES",
  value: "50° 16' 01\" N / 119° 16' 19\" W"
}, {
  label: "SIGNAL",
  value: "solobservatory@gmail.com"
}, {
  label: "FREQUENCY",
  value: "Once A Month"
}, {
  label: "LIVESTREAMS",
  value: "Imaging & Editing"
}, {
  label: "STATUS",
  value: "Coming Online..."
}];
function ReachOutForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border-t border-b border-[var(--border)] py-12">
        <p className="font-mono text-xs uppercase tracking-[0.15em] text-[var(--muted-foreground)] mb-4">
          SIGNAL — RECEIVED
        </p>
        <p className="text-foreground text-xl font-default font-light leading-snug mb-8">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border border-[var(--border)] bg-transparent text-foreground hover:bg-transparent hover:border-[var(--nebula-violet)] px-9 py-5 h-auto font-default font-light tracking-[0.04em] rounded-md">
          Send another transmission
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <FormField control={control} name="parent_name" rules={{
      required: "Observer name is required"
    }} render={({
      field
    }) => <FormItem className="grid gap-3">
            <FormLabel className="font-mono text-[11px] uppercase tracking-[0.15em] text-[var(--muted-foreground)] font-normal">
              Observer Name
            </FormLabel>
            <FormControl>
              <Input placeholder="e.g., Dr. Helena Vance" {...field} className="border-0 border-b border-[var(--border)] bg-transparent px-0 py-4 h-auto text-base text-foreground focus-visible:ring-0 focus-visible:border-[var(--corona-orange)] placeholder:text-[var(--muted-foreground)]/50 rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="child_name" render={({
      field
    }) => <FormItem className="grid gap-3">
            <FormLabel className="font-mono text-[11px] uppercase tracking-[0.15em] text-[var(--muted-foreground)] font-normal">
              Affiliation or Topic
            </FormLabel>
            <FormControl>
              <Input placeholder="e.g., Solar Chromosphere" {...field} className="border-0 border-b border-[var(--border)] bg-transparent px-0 py-4 h-auto text-base text-foreground focus-visible:ring-0 focus-visible:border-[var(--nebula-violet)] placeholder:text-[var(--muted-foreground)]/50 rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem className="grid gap-3">
            <FormLabel className="font-mono text-[11px] uppercase tracking-[0.15em] text-[var(--muted-foreground)] font-normal">
              Digital Signal (Email)
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="observer@cosmos.net" {...field} className="border-0 border-b border-[var(--border)] bg-transparent px-0 py-4 h-auto text-base text-foreground focus-visible:ring-0 focus-visible:border-[var(--photon-blue)] placeholder:text-[var(--muted-foreground)]/50 rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="grid gap-3">
            <FormLabel className="font-mono text-[11px] uppercase tracking-[0.15em] text-[var(--muted-foreground)] font-normal">
              Direct Frequency (Phone)
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-1234" {...field} className="border-0 border-b border-[var(--border)] bg-transparent px-0 py-4 h-auto text-base text-foreground focus-visible:ring-0 focus-visible:border-[var(--border)] placeholder:text-[var(--muted-foreground)]/50 rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please provide details for your inquiry",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      },
      maxLength: {
        value: 1000,
        message: "Message cannot exceed 1000 characters"
      }
    }} render={({
      field
    }) => <FormItem className="grid gap-3">
            <FormLabel className="font-mono text-[11px] uppercase tracking-[0.15em] text-[var(--muted-foreground)] font-normal">
              Inquiry Details
            </FormLabel>
            <FormControl>
              <Textarea rows={4} placeholder="Describe your observation or collaboration request…" {...field} className="border-0 border-b border-[var(--border)] bg-transparent px-0 py-4 min-h-0 text-base text-foreground resize-none focus-visible:ring-0 focus-visible:border-[var(--chromosphere-ember)] placeholder:text-[var(--muted-foreground)]/50 rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="border-t border-b border-[var(--destructive)] py-4">
          <p className="font-mono text-xs uppercase tracking-[0.15em] text-[var(--destructive)]">
            {submitError}
          </p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="bg-[var(--starlight-white)] text-[var(--void-black)] hover:bg-[var(--photon-blue)] hover:scale-[1.02] transition-all duration-300 px-9 py-5 h-auto text-base font-default font-medium tracking-[0.04em] w-full sm:w-auto rounded-md">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Transmitting
            </> : "Send Transmission"}
        </Button>
        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.15em] text-[var(--muted-foreground)]">
          Response latency: 24 hours · UTC-7
        </p>
      </div>
    </Form>;
}
export default function InquiryDesk() {
  return;
}